import { API, Storage } from 'aws-amplify';
import { v4 as uuidv4 } from 'uuid';
import {
  createDealFile,
  editReportWithPrompt,
  generateTaskListReport,
  respondToCustomerComment,
  sendDamageReport,
  updateDeal,
  updateDealFile,
} from '../../graphql/mutations';
import {
  getReportEditContext,
  listDealCommentByReportID,
  listDealFilesByDealIDType,
  listDealFilesByReportID,
} from '../../graphql/queries';
import {
  compareVersions,
  extractDealContacts,
  fileNameForVersion,
  incrementVersion,
  prepareReportForSave,
  profileValue,
  resolveReportBaseId,
  versionFromFile,
} from '../lib/report/report-utils';
import type { PhotoRekey, TaskReassignment } from '../lib/report/report-ops';
import type { Report, ReportFile } from '../lib/report/types';

// All report data access for the V2 report editor. Save/version semantics
// mirror mobile utils/reportManager.ts so both clients extend one lineage.

const me = () => (global as any).me || {};

const gql = (query: string, variables: Record<string, unknown>) =>
  API.graphql({ query, variables }) as Promise<any>;

async function listAll(
  query: string,
  field: string,
  variables: Record<string, unknown>
): Promise<any[]> {
  const items: any[] = [];
  let nextToken: string | null = null;
  do {
    const res = await gql(query, { ...variables, limit: 500, nextToken });
    const page = res?.data?.[field];
    items.push(...(page?.items || []));
    nextToken = page?.nextToken || null;
  } while (nextToken);
  return items;
}

/** Every DealFile row of a report lineage (soft-deleted rows included). */
const listLineage = (reportID: string, dealId: string) =>
  listAll(listDealFilesByReportID, 'listDealFilesByReportID', {
    reportID,
    filter: { orgID: { eq: me().orgID }, dealID: { eq: dealId } },
  });

function latestFile<T extends ReportFile>(files: T[]): T | null {
  return files.reduce<T | null>((best, file) => {
    if (!best) {
      return file;
    }
    const cmp = compareVersions(versionFromFile(file), versionFromFile(best));
    const newer =
      cmp > 0 ||
      (cmp === 0 &&
        new Date(file.updatedAt || 0).getTime() >
          new Date(best.updatedAt || 0).getTime());
    return newer ? file : best;
  }, null);
}

async function fetchReportJson(s3Key: string): Promise<Report> {
  const res: any = await Storage.get(s3Key, { download: true });
  return JSON.parse(await res.Body.text());
}

/**
 * Load the newest saved version of the report `file` belongs to. The deal's
 * embedded file list can be stale or capped, so the lineage is re-read.
 */
export async function fetchLatestReport(
  file: ReportFile,
  dealId: string
): Promise<Report> {
  const baseId = file.reportID || resolveReportBaseId({ id: file.id });
  let target: ReportFile = file;
  if (baseId) {
    const rows = (await listLineage(baseId, dealId)).filter(
      (f) => !f.isDeleted
    );
    target = latestFile([file, ...rows]) || file;
  }
  const report = await fetchReportJson(target.url);
  return {
    ...report,
    id: target.id,
    dealId,
    reportType: report.reportType || target.reportType || undefined,
  };
}

/** Same last-resort inference as mobile for legacy blobs without a type. */
const inferReportType = (title?: string) =>
  !title ? null : /task list/i.test(title) ? 'TASK_LIST' : 'AUDIO';

/**
 * Save `report` as a new version (S3 JSON + DealFile row). Never writes at or
 * below the lineage head, so a stale editor can't overwrite another version.
 * Last save wins, as agreed for concurrent mobile/web edits.
 */
export async function saveReportVersion(
  report: Report,
  reassignments: TaskReassignment[] = [],
  rekeys: PhotoRekey[] = []
): Promise<Report> {
  const { id: userId, orgID } = me();
  const dealId = report.dealId;
  const baseId = resolveReportBaseId(report);
  if (!baseId || !dealId || !orgID) {
    throw new Error('Missing report identity, deal, or org');
  }

  // Annotated photos first (blocking): the editor shows each DealFile's
  // current key, so a version saved without its re-key would look unannotated.
  // Same as mobile's replaceFileImage: same id, new key; old S3 object kept.
  await Promise.all(
    rekeys.map((r) =>
      gql(updateDealFile, {
        input: {
          id: r.dealFileId,
          url: r.key,
          type: 'image/jpeg',
          lastUpdatedBy: userId,
        },
      })
    )
  );

  let version = incrementVersion(report.version);
  const head = latestFile(await listLineage(baseId, dealId));
  const headVersion = head ? versionFromFile(head) : null;
  if (headVersion && compareVersions(version, headVersion) <= 0) {
    version = incrementVersion(headVersion);
  }

  const fileName = fileNameForVersion(baseId, version);
  const s3Key = `reports/${fileName}`;
  const now = new Date().toISOString();
  const finalReport = prepareReportForSave({
    ...report,
    id: fileName,
    reportID: baseId,
    version,
    updatedAt: now,
  });

  await Storage.put(s3Key, JSON.stringify(finalReport, null, 2), {
    contentType: 'application/json',
    level: 'public',
  });

  const reportType =
    report.reportType || head?.reportType || inferReportType(report.title);
  const input = {
    id: fileName,
    orgID,
    dealID: dealId,
    reportID: baseId,
    reportType,
    type: 'report',
    description: report.title,
    category: 'report',
    url: s3Key,
    sequence: 0,
    tags: ['report', `v${version}`],
    createdBy: report.createdBy || userId,
    lastUpdatedBy: userId,
    isDeleted: false,
    createdAt: report.createdAt,
    updatedAt: now,
    fileTimestampEpoch: Date.now(),
  };
  try {
    await gql(createDealFile, { input });
  } catch (err: any) {
    // Only reachable if a concurrent save claimed this version since the
    // preflight; the S3 object is already ours, so point the row at it.
    const type = err?.errors?.[0]?.errorType || '';
    if (!type.includes('ConditionalCheckFailed')) {
      throw err;
    }
    await gql(updateDealFile, {
      input: {
        id: fileName,
        reportID: baseId,
        reportType,
        type: 'report',
        description: report.title,
        category: 'report',
        url: s3Key,
        sequence: 0,
        tags: input.tags,
        lastUpdatedBy: userId,
        isDeleted: false,
        updatedAt: now,
        fileTimestampEpoch: input.fileTimestampEpoch,
      },
    });
  }

  // Non-blocking follow-ups, same as mobile: bump the deal to the top of the
  // pipeline, and move photos' DealFiles to the task they were moved under.
  gql(updateDeal, { input: { id: dealId, updatedAt: now } }).catch((e) =>
    console.warn('[report] deal timestamp bump failed', e)
  );
  reassignments.forEach((r) =>
    gql(updateDealFile, {
      input: { id: r.dealFileId, task_id: r.taskId, lastUpdatedBy: userId },
    }).catch((e) => console.warn('[report] photo task reassignment failed', e))
  );

  return { ...finalReport, dealId };
}

// ── Photos ──────────────────────────────────────────────────

export interface DealImage {
  id: string;
  key: string;
  description?: string;
  createdAt?: string;
  /** Task the photo is filed under on the deal (null = not linked). */
  taskId: string | null;
}

const IMAGE_EXT_RE = /\.(jpe?g|png|gif|webp|heic|heif)$/i;

export async function listDealImages(dealId: string): Promise<DealImage[]> {
  const rows = await listAll(
    listDealFilesByDealIDType,
    'listDealFilesByDealIDType',
    {
      dealID: dealId,
      filter: { isDeleted: { ne: true } },
    }
  );
  return rows
    .filter((f) => {
      const key = f.url || f.id || '';
      return (
        key &&
        f.type !== 'report' &&
        !['measurement_report', 'estimate'].includes(f.category) &&
        (String(f.type || '').startsWith('image/') || IMAGE_EXT_RE.test(key))
      );
    })
    .sort(
      (a, b) =>
        new Date(b.createdAt || 0).getTime() -
        new Date(a.createdAt || 0).getTime()
    )
    .map((f) => ({
      id: f.id,
      key: f.url || f.id,
      description: f.description || undefined,
      createdAt: f.createdAt,
      taskId: f.task_id || null,
    }));
}

/** Downscale to ≤2400px JPEG like mobile's upload compression; original on failure. */
async function compressImage(file: File): Promise<Blob> {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, 2400 / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas
      .getContext('2d')
      ?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.85)
    );
    return blob || file;
  } catch {
    return file;
  }
}

/**
 * Upload a photo as a real deal photo (DealFile), matching mobile's
 * DealFileService.addFile: uuid id, bare uuid key in the public area.
 */
export async function uploadDealPhoto(
  file: File,
  dealId: string
): Promise<DealImage> {
  const { id: userId, orgID } = me();
  const body = await compressImage(file);
  const isJpeg = body.type === 'image/jpeg';
  const ext = isJpeg
    ? 'jpg'
    : file.name.split('.').pop()?.toLowerCase() || 'jpg';
  const id = uuidv4();
  const key = `${uuidv4()}.${ext}`;
  await Storage.put(key, body, {
    contentType: isJpeg ? 'image/jpeg' : file.type || 'image/jpeg',
    level: 'public',
  });
  await gql(createDealFile, {
    input: {
      id,
      orgID,
      dealID: dealId,
      deal_id: dealId,
      task_id: null,
      type: isJpeg ? 'image/jpeg' : file.type || 'image/jpeg',
      url: key,
      description: '',
      tags: [],
      category: '',
      sequence: 0,
      createdBy: userId,
      lastUpdatedBy: userId,
      isDeleted: false,
      fileTimestampEpoch: Date.now(),
    },
  });
  return { id, key, taskId: null };
}

/** A stored image's bytes (via Amplify, so a canvas can export it untainted). */
export async function downloadImage(key: string): Promise<Blob> {
  const res: any = await Storage.get(key, { level: 'public', download: true });
  return res.Body as Blob;
}

/** Upload an annotated photo under a fresh key; the DealFile moves to it on save. */
export async function uploadAnnotatedImage(image: Blob): Promise<string> {
  const key = `${uuidv4()}.jpg`;
  await Storage.put(key, image, { contentType: 'image/jpeg', level: 'public' });
  return key;
}

/** Custom cover photo — same location mobile's HeroImageSelector uses. */
export async function uploadHeroImage(file: File): Promise<string> {
  const body = await compressImage(file);
  const key = `reports/hero-images/${uuidv4()}.jpg`;
  await Storage.put(key, body, { contentType: 'image/jpeg', level: 'public' });
  return key;
}

// ── AI editing ──────────────────────────────────────────────

export interface ChatTurn {
  turnId: string;
  role: 'user' | 'assistant';
  text: string;
  status?: string;
  resultingVersion?: string;
}

const toTurn = (t: any): ChatTurn => ({
  turnId: t?.turnId || `t-${Math.random().toString(36).slice(2)}`,
  role: t?.role === 'assistant' ? 'assistant' : 'user',
  text: t?.text || '',
  status: t?.status || undefined,
  resultingVersion: t?.resultingVersion || undefined,
});

export async function fetchEditThread(reportId: string): Promise<ChatTurn[]> {
  const res = await gql(getReportEditContext, { id: reportId });
  return (res?.data?.getReportEditContext?.thread || []).map(toTurn);
}

export interface EditResult {
  success: boolean;
  reply?: string;
  version?: string;
  error?: string;
}

export async function requestAiEdit(params: {
  reportId: string;
  dealId: string;
  message: string;
}): Promise<EditResult> {
  const { id: userId, orgID } = me();
  const res = await gql(editReportWithPrompt, {
    request: JSON.stringify({
      reportID: params.reportId,
      dealID: params.dealId,
      message: params.message,
      userId,
      orgID,
    }),
  });
  const raw = res?.data?.editReportWithPrompt;
  return (
    (typeof raw === 'string' ? JSON.parse(raw) : raw) || { success: false }
  );
}

/** AWSJSON results arrive as (sometimes double-)stringified JSON. */
function parseJson(raw: unknown): any {
  let value = raw;
  for (let i = 0; i < 2 && typeof value === 'string'; i++) {
    value = JSON.parse(value);
  }
  return value;
}

// ── Sharing (sendDamageReport — same lambda mobile uses) ────

/**
 * Signed customer link (token valid ~12 months). The getShareUrl fast path
 * only signs a URL — it sends nothing. Falls back to the unsigned link like
 * mobile so sharing is never blocked.
 */
export async function getReportShareUrl(
  reportFileId: string,
  phone?: string
): Promise<string> {
  try {
    const res = await gql(sendDamageReport, {
      request: JSON.stringify({
        reportID: reportFileId,
        getShareUrl: true,
        ...(phone ? { phoneNumber: phone } : {}),
      }),
    });
    const url = parseJson(res?.data?.sendDamageReport)?.shareUrl;
    if (url) {
      return url;
    }
  } catch (e) {
    console.warn('[report] signed share link failed, using unsigned', e);
  }
  const base = `https://damage-report.paiv.ai?reportFileName=${encodeURIComponent(
    reportFileId
  )}`;
  return phone ? `${base}&phone=${encodeURIComponent(phone)}` : base;
}

/** Email the customer a link to the report (the lambda sends it). */
export async function emailReport(params: {
  reportFileId: string;
  dealId: string;
  contactName?: string;
  email: string;
}): Promise<void> {
  await gql(sendDamageReport, {
    request: JSON.stringify({
      reportID: params.reportFileId,
      dealID: params.dealId,
      contactName: params.contactName,
      emailAddress: params.email,
    }),
  });
}

/**
 * Customer phone/email from the deal's flex fields, located through the
 * deal category's field types (port of mobile utils/contactExtraction.ts).
 */
export async function getDealContacts(
  deal: any
): Promise<{ phone: string; email: string }> {
  const empty = { phone: '', email: '' };
  if (!deal?.categoryID || !deal?.flexAttributes) {
    return empty;
  }
  const res = await gql(
    /* GraphQL */ `
      query GetCategoryFlexFields($id: ID!) {
        getStageCategory(id: $id) {
          id
          flexAttributes
        }
      }
    `,
    { id: deal.categoryID }
  );
  return extractDealContacts(
    parseJson(res?.data?.getStageCategory?.flexAttributes)?.config?.deal
      ?.flexFields || [],
    parseJson(deal.flexAttributes) || {}
  );
}

// ── Customer comments (texts about the shared report) ───────

export interface ReportComment {
  id: string;
  comment: string;
  name: string;
  contactInformation: string;
  /** "inbound_sms" when the customer texted it in. */
  lastUpdatedBy?: string;
  createdAt: string;
}

export async function listReportComments(
  reportId: string
): Promise<ReportComment[]> {
  let items: any[] = [];
  try {
    items = await listAll(
      listDealCommentByReportID,
      'listDealCommentByReportID',
      {
        reportID: reportId,
        sortDirection: 'ASC',
      }
    );
  } catch (err: any) {
    // AppSync can return partial data alongside errors for null fields.
    items = err?.data?.listDealCommentByReportID?.items || [];
    if (!items.length && !err?.data) {
      throw err;
    }
  }
  return items
    .filter((c) => c && !c.isDeleted && c.name?.trim())
    .map((c) => ({
      id: c.id,
      comment: c.comment || '',
      name: c.name,
      contactInformation: c.contactInformation || '',
      lastUpdatedBy: c.lastUpdatedBy || undefined,
      createdAt: c.createdAt,
    }))
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

/** Reply to the customer; the backend texts it to `phone` (mobile parity). */
export async function respondToReportComment(params: {
  reportId: string;
  comment: string;
  phone: string;
}): Promise<void> {
  const { orgID, name, email, phoneNumber } = me();
  const repPhone = profileValue(phoneNumber);
  const res = await gql(respondToCustomerComment, {
    request: JSON.stringify({
      orgID,
      reportID: params.reportId,
      comment: params.comment,
      name,
      contactInformation: repPhone || email || name || 'unknown',
      phoneNumber: params.phone,
      salesRepPhone: repPhone,
    }),
  });
  const payload = parseJson(res?.data?.respondToCustomerComment);
  const envelope =
    payload && payload.body !== undefined ? parseJson(payload.body) : payload;
  if (!(envelope?.success === true || envelope?.comment)) {
    throw new Error(envelope?.error || 'Reply was not sent');
  }
}

// ── Delete / regenerate ─────────────────────────────────────

/**
 * Soft-delete every version of a report (isDeleted on each DealFile row,
 * like mobile's deleteReportAllVersions). Nothing is removed from S3 or the
 * table, so it can be restored by flipping the flag back.
 */
export async function deleteReportVersions(
  reportId: string,
  dealId: string
): Promise<number> {
  const rows = (await listLineage(reportId, dealId)).filter(
    (f) => !f.isDeleted
  );
  const now = new Date().toISOString();
  await Promise.all(
    rows.map((f) =>
      gql(updateDealFile, {
        input: {
          id: f.id,
          isDeleted: true,
          updatedAt: now,
          lastUpdatedBy: me().id,
        },
      })
    )
  );
  return rows.length;
}

/** Rebuild the deal's task-list report from the latest inspection data. */
export async function regenerateTaskListReport(dealId: string): Promise<void> {
  const res = await gql(generateTaskListReport, {
    request: JSON.stringify({ dealID: dealId }),
  });
  const result = parseJson(res?.data?.generateTaskListReport);
  if (!result?.success) {
    throw new Error(result?.error || 'Report regeneration failed');
  }
}
